import React, { useState, useEffect, useContext } from "react"
import BillingMobileView from "./BillingMobileView"
import BillingPreviewElement from "./BillingPreviewElement"
import BackToButton from "../../BackToButton"
import ContactPrimaryDetails from "./ContactPrimaryDetails"
import MeetingPreviewDetails from "./MeetingPreviewDetails"
import MeetingsPreviewElement from "./MeetingsPreviewElement"
import moment from "moment"
import NewMeeting from "../../scheduling/new-meeting/NewMeeting"
import NotesPopover from "../../../components/notes/NotesPopover"
import NotesPreviewElement from "./NotesPreviewElement"
import ProfileMessageForm from "../../auth/register-form/profile-message-form"
import ServicesPreviewElement from "./ServicesPreviewElement"
import useLoading from "../../../hooks/useLoading"
import useScrollToTop from "../../../hooks/useScrollToTop"
import { ContactRequests, MeetingRequests } from "../../../api/app.service"
import { Meeting } from "../../../types/meetingTypes"
import { MeetingType } from "../../../types/meetingTypes"
import { useParams, useLocation } from "react-router-dom"
import { UserCtx } from "../../../context/userContext"
import { useToast } from "../../../hooks/useToast"

interface Props {
  setActiveContactProfile: React.Dispatch<React.SetStateAction<any>>
  connectAndRedirect?: any
}

const DynamicContactProfile: React.FC<Props> = ({
  setActiveContactProfile,
  connectAndRedirect,
}) => {
  const { contact_id } = useParams()
  const location = useLocation()
  const { user } = useContext(UserCtx)
  const { startLoading, stopLoading } = useLoading()
  const { toast } = useToast()
  const [contact, setContact] = useState<any>(null)
  const [callGetMemberNotes, setCallGetMemberNotes] = useState(false)
  const [showNotesPopover, setShowNotesPopover] = useState<boolean>(false)
  const [showToday, setShowToday] = useState<boolean>(true)
  const [showBilling, setShowBilling] = useState<boolean>(false)
  const [showNoteModal, setShowNoteModal] = useState<boolean>(false)
  const [isNewNote, setIsNewNote] = useState<boolean>(false)
  const [openUpcomingDetails, setOpenUpcomingDetails] = useState<boolean>(false)
  const [upcomingSessionDetailViewId, setUpcomingSessionDetailViewId] =
    useState<number | null>(null)
  const [selectedState, setSelectedState] = useState<string>("upcoming")
  const [serviceState, setServiceState] = useState<string>("coach")
  const [meetings, setMeetings] = useState<any[]>([])
  const [filteredMeetings, setFilteredMeetings] = useState<any[]>([])
  const [showRescheduleMeeting, setShowRescheduleMeeting] =
    useState<boolean>(false)
  const [clickReschedule, setClickReschedule] = useState<boolean>(false)
  const [clickTodayReschedule, setClickTodayReschedule] =
    useState<boolean>(false)
  const [backToContacts, setBackToContacts] = useState<boolean>(false)
  const [keepBackToButton, setKeepBackToButton] = useState<boolean>(false)
  const [showNewMeeting, setShowNewMeeting] = useState<boolean>(false)
  const [newMeetingType, setNewMeetingType] = useState<MeetingType>("custom")
  const [profileMessageFormModal, setprofileMessageFormModal] =
    useState<boolean>(false)
  const [activeDate, setActiveDate] = useState<Date>(
    moment(new Date()).startOf("day").tz(user?.timezone?.value)?.toDate()
  )

  interface C2CContact {
    avatar_url: string
    contact_date: Date
    contact_name: string
    contact_type: string
    email: string
    endpoint_slug: string | null
    has_avatar: boolean
    profile_connection_id: number
    profile_id: string
    profile_type: string | null
    public_profile_published: boolean
    status: string
    user_color: string
    user_id: string
    original_contact: any
  }

  const filterMeetingsForProfile = () => {
    if (meetings.length > 0) {
      const now = moment(Date.now())
      let filteredMeetings: any[] = []
      meetings.forEach((meeting: any) => {
        const participants_emails = meeting.contacts.map(
          (participant: any) => participant.email
        )
        if (meeting.is_managed) {
          if (selectedState === "upcoming") {
            if (
              participants_emails.includes(contact.email) &&
              meeting.startTime > now
            ) {
              filteredMeetings.push(meeting)
            }
          } else if (selectedState === "past") {
            if (
              participants_emails.includes(contact.email) &&
              meeting.startTime < now
            ) {
              filteredMeetings.push(meeting)
            }
          }
        }
      })

      if (selectedState === "upcoming") {
        filteredMeetings.sort((a: any, b: any) => {
          return moment.utc(a.startTime).diff(moment.utc(b.startTime))
        })
      } else {
        filteredMeetings.sort((a: any, b: any) => {
          return moment.utc(b.startTime).diff(moment.utc(a.startTime))
        })
      }

      setFilteredMeetings(filteredMeetings)
    }
  }

  const contactObj = (): C2CContact => {
    let contact_c2c: C2CContact = {
      avatar_url: user.avatar_url,
      contact_date: contact.contact_date,
      contact_name: user.firstName,
      contact_type: contact.contact_type,
      email: user.email,
      endpoint_slug: user?.coachProfile.endpoint_slug,
      has_avatar: user.hasAvatar,
      profile_connection_id: contact.profile_connection_id,
      profile_id: user.activeProfileId,
      profile_type: user.activeProfile,
      public_profile_published: user.isSubscribed,
      status: contact.status,
      user_color: user.userColor,
      user_id: user.user_id,
      original_contact: contact,
    }
    return contact_c2c
  }

  const calcDuration = (endTime: any, startTime: any) => {
    const calculated = moment
      .utc(endTime)
      .diff(moment.utc(startTime), "minutes")
    let duration
    if (calculated >= 1) {
      duration = calculated
    } else {
      duration = 1
    }
    return duration
  }

  const getMeetings = () => {
    if (user) {
      const startOfActiveDate = moment(activeDate)
        ?.startOf("day")
        ?.utc()
        ?.tz(user?.timezone?.value)
        ?.toISOString()
      const endOfSixMonths = moment(activeDate)
        ?.add(6, "month")
        ?.endOf("day")
        ?.utc()
        ?.tz(user.timezone.value)
        ?.toISOString()

      MeetingRequests.getMeetings({
        user_id: parseInt(user.user_id),
        profile_id: user.activeProfileId,
        from_date: startOfActiveDate,
        to_date: endOfSixMonths,
      })
        .then((data) => {
          const meetings: Array<Meeting> = []
          data.meetings.forEach((m) => {
            // parse UTC string from db to a JS Date object with time adjusted for user timezone
            let duration = calcDuration(m.end_time, m.start_time)
            let parsedStartTime = m.start_time
            let parsedEndTime = m.end_time
            let parsedDate = m.start_time
            if (duration < 1440) {
              parsedStartTime = moment
                .utc(m.start_time)
                .tz(user.timezone.value)
                .toDate()

              if (m.end_time) {
                parsedEndTime = moment
                  .utc(m.end_time)
                  .tz(user.timezone.value)
                  .toDate()
              }
              parsedDate = moment
                .utc(m.start_time)
                .tz(user.timezone.value)
                .toDate()
            } else {
              parsedStartTime = moment
                .tz(m.start_time, "YYYY-MM-DD", user.timezone.value)
                .toDate()

              if (m.end_time) {
                parsedEndTime = moment
                  .tz(m.end_time, "YYYY-MM-DD", user.timezone.value)
                  .toDate()
              }
              parsedDate = moment
                .tz(m.start_time, "YYYY-MM-DD", user.timezone.value)
                .toDate()
            }
            const obj: Meeting = {
              id: m.meeting_id,
              inProgress: m.in_progress,
              meetingType: m.meeting_type,
              coach_user_id: m.coach_user_id,
              type: m.type,
              contacts: m.participants,
              meetingName: m.name,
              status: m.status,
              medium: m.medium,
              isCreator: m.is_creator,
              date: parsedDate,
              startTime: parsedStartTime,
              endTime: parsedEndTime,
              meetingEnded: m.meeting_ended,
              duration: duration,
              // TODO need to know what external calendar it is coming from (e.g. google, office365) in order to use different views
              // or decide to only have one color for all non_managed calendars
              // 9/23/2022: For 2.0 launch, will just use the is_managed property to distinguish between Zoee-managed and others. Return to this property in future versions.
              calendar: `${m.is_managed ? "zoee" : "google"}`,
              timezone: user.timezone,
              service_usage_id: m.service_usage_id,
              service_details: m.service_details,
              // TODO backend for notifications not ready yet
              notifications: [],
              belongs_to_profile: m.belongs_to_profile,
              is_managed: m.is_managed,
              stripe_product_id: m.stripe_product_id,
            }
            meetings.push(obj)
          })

          const sortedMeetings = meetings.sort((a: any, b: any) => {
            const bDate: any = new Date(b.startTime)
            const aDate: any = new Date(a.startTime)
            return aDate - bDate
          })

          setMeetings(sortedMeetings)
        })
        .catch((ex) => {
          console.error(ex)
          toast.error(ex.response.data.message)
        })
    }
  }

  const getContact = (profileId: string) => {
    if (user?.coachProfile || user?.memberProfile) {
      startLoading()
      ContactRequests.getContactList({
        profile_id: user.activeProfileId,
        profile_type: user.activeProfile,
      })
        .then((data) => {
          const foundContact = data.contact_list.find((c: any) => {
            return c.profile_id === parseInt(profileId)
          })

          // TODO double check that we want user to only be able to see contact profile of connected contacts
          if (foundContact.contact_type === "connected") {
            // TODO can probably consolidate these two state variables
            setContact(foundContact)
            setActiveContactProfile(foundContact)
          }
          stopLoading()
        })
        .catch((ex) => {
          console.error(ex)
          toast.error(ex.response.data.message)
          stopLoading()
        })
    }
  }

  let state: any = null
  if (location && location.state) {
    state = location.state
  }

  useEffect(() => {
    if (state?.status === "success") {
      toast.success("Subscription purchased!")
    }
    if (state?.contact) {
      setContact(state.contact)
    } else {
      if (contact_id) {
        getContact(contact_id)
      }
    }
  }, [state])

  useEffect(() => {
    filterMeetingsForProfile()
  }, [meetings, selectedState])

  useEffect(() => {
    if (showNoteModal) {
      document.body.style.overflow = "hidden"
    } else {
      document.body.style.overflow = "auto"
    }
    return () => {
      document.body.style.overflow = "auto"
    }
  }, [showNoteModal])

  const meeting = meetings?.find((s) => s.id === upcomingSessionDetailViewId)

  useScrollToTop()

  if (showBilling) {
    return (
      <BillingMobileView setShowBilling={setShowBilling} contact={contact} />
    )
  } else {
    return (
      <>
        {showRescheduleMeeting ? (
          <NewMeeting
            newMeetingType="custom"
            setShowNewMeeting={setShowNewMeeting}
            setActiveSessionDetailViewId={setUpcomingSessionDetailViewId}
            existingMeetingData={meeting}
            initialStep={"availability"}
            isEditing={true}
            setNewMeetingType={setNewMeetingType}
            showRescheduleMeeting={showRescheduleMeeting}
            setShowRescheduleMeeting={setShowRescheduleMeeting}
            clickReschedule={clickReschedule}
            setClickReschedule={setClickReschedule}
            clickTodayReschedule={clickTodayReschedule}
            backToContacts={backToContacts}
            contact={contact}
            keepBackToButton={keepBackToButton}
          />
        ) : (
          <>
            {openUpcomingDetails ? (
              <MeetingPreviewDetails
                meeting={meetings?.find(
                  (s) => s.id === upcomingSessionDetailViewId
                )}
                setOpenUpcomingDetails={setOpenUpcomingDetails}
                setUpcomingSessionDetailViewId={setUpcomingSessionDetailViewId}
                getMeetings={getMeetings}
                setBackToContacts={setBackToContacts}
                backToContacts={backToContacts}
                contact={contact}
                keepBackToButton={keepBackToButton}
              />
            ) : (
              <div className="px-[20px] md:px-0 md:w-[1080px] md:max-w-[90%] mx-auto flex flex-col pb-24">
                {contact ? (
                  <>
                    <div className="mb-[24px]">
                      <ContactPrimaryDetails
                        contact={contact}
                        setShowBilling={setShowBilling}
                      />
                    </div>

                    {/* MOBILE LAYOUT */}

                    <div className="md:hidden">
                      {showToday && (
                        <>
                          <div className="mb-[16px]">
                            <MeetingsPreviewElement
                              contact={contact}
                              setOpenUpcomingDetails={setOpenUpcomingDetails}
                              setUpcomingSessionDetailViewId={
                                setUpcomingSessionDetailViewId
                              }
                              filteredMeetings={filteredMeetings}
                              getMeetings={getMeetings}
                              selectedState={selectedState}
                              setMeetings={setMeetings}
                              setSelectedState={setSelectedState}
                              setShowRescheduleMeeting={
                                setShowRescheduleMeeting
                              }
                              setBackToContacts={setBackToContacts}
                            />
                          </div>
                        </>
                      )}
                    </div>

                    {/* DESKTOP LAYOUT */}
                    <div className="">
                      <div className="hidden md:block mb-[32px]">
                        <MeetingsPreviewElement
                          contact={contact}
                          setOpenUpcomingDetails={setOpenUpcomingDetails}
                          setUpcomingSessionDetailViewId={
                            setUpcomingSessionDetailViewId
                          }
                          filteredMeetings={filteredMeetings}
                          getMeetings={getMeetings}
                          selectedState={selectedState}
                          setMeetings={setMeetings}
                          setSelectedState={setSelectedState}
                          setShowRescheduleMeeting={setShowRescheduleMeeting}
                          setBackToContacts={setBackToContacts}
                        />
                      </div>

                      {user.activeProfile === "coach" &&
                      contact.profile_type === "coach" ? (
                        <>
                          <div className="w-full main-shadow rounded-[16px] px-[16px] py-[24px] md:p-[40px] relative">
                            <h2 className="hidden md:block font-semibold text-[28px]">
                              {contact.profile_type === "coach" &&
                                user.activeProfile === "coach" &&
                                "Services"}
                            </h2>
                            <div className=" items-center gap-[10px] mb-[32px]">
                              <div className="relative flex justify-center items-center w-full rounded-[20px] h-[50px] p-1">
                                <button
                                  className={`w-[50%] h-full rounded-[18px] font-bold
																${
                                  serviceState === "coach"
                                    ? "bg-blurple text-[white]"
                                    : "bg-grayFlash text-[black]"
                                } `}
                                  onClick={() => setServiceState("coach")}
                                >
                                  {contact.contact_name}
                                </button>
                                <button
                                  className={`w-[50%] h-full rounded-[18px] font-bold
																${
                                  serviceState === "client"
                                    ? "bg-blurple text-[white]"
                                    : "bg-grayFlash text-[black]"
                                }`}
                                  onClick={() => setServiceState("client")}
                                >
                                  {user.firstName}
                                </button>
                              </div>
                            </div>

                            {serviceState === "coach" && (
                              <>
                                <ServicesPreviewElement
                                  contact={contact}
                                  serviceState={serviceState}
                                />

                                <div className="main-shadow">
                                  <BillingPreviewElement
                                    contact={contact}
                                    serviceState={serviceState}
                                  />
                                </div>
                              </>
                            )}
                            {serviceState === "client" && (
                              <>
                                <ServicesPreviewElement
                                  contact={contactObj()}
                                  serviceState={serviceState}
                                />

                                <div className="main-shadow">
                                  <BillingPreviewElement
                                    contact={contact}
                                    serviceState={serviceState}
                                  />
                                </div>
                              </>
                            )}
                          </div>
                        </>
                      ) : (
                        <div className="mb-[32px]">
                          <>
                            <ServicesPreviewElement
                              contact={contact}
                              serviceState={serviceState}
                            />
                            <div className="main-shadow">
                              <BillingPreviewElement
                                contact={contact}
                                serviceState={serviceState}
                              />
                            </div>
                          </>
                        </div>
                      )}

                      {showNotesPopover && (
                        <NotesPopover
                          setShowNotesPopover={setShowNotesPopover}
                          setCallGetMemberNotes={setCallGetMemberNotes}
                          contact={contact}
                          showNoteModal={showNoteModal}
                          setShowNoteModal={setShowNoteModal}
                          isNewNote={isNewNote}
                          setIsNewNote={setIsNewNote}
                        />
                      )}
                      <div className="w-full mb-[32px]">
                        <NotesPreviewElement
                          contact={contact}
                          setShowNotesPopover={setShowNotesPopover}
                          callGetMemberNotes={callGetMemberNotes}
                          setCallGetMemberNotes={setCallGetMemberNotes}
                          showNoteModal={showNoteModal}
                          setShowNoteModal={setShowNoteModal}
                          isNewNote={isNewNote}
                          setIsNewNote={setIsNewNote}
                        />
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <BackToButton
                      to="/coach/contacts"
                      prev="Contact Not Found"
                      current="Contacts"
                    />
                    {/* TODO improve uiux / messaging */}
                    <h3 className="mt-[24px] font-bold">No contact found</h3>
                  </>
                )}
              </div>
            )}
          </>
        )}
        {profileMessageFormModal && (
          <ProfileMessageForm
            showConfirm={profileMessageFormModal}
            setShowConfirm={setprofileMessageFormModal}
            coachName={`${contact?.first_name} ${contact?.last_name}`}
            connectAndRedirect={connectAndRedirect}
          />
        )}
      </>
    )
  }
}

export default DynamicContactProfile
